import { makePersisted } from "@solid-primitives/storage";
import { type Accessor, createMemo, createSignal } from "solid-js";
import { useUserMode } from "~/hooks/useUserMode";

/** What to show for in-app help: full UI, only hide Learn more rows, only hide StTooltips, or both. */
type HelpTooltipsPreference = "show_all" | "hide_learn_more" | "hide_tooltips" | "hide_all";

const STORAGE_KEY = "help-tooltips-preference";

function normalizeParsed(v: unknown): HelpTooltipsPreference | undefined {
  const x: unknown = Array.isArray(v) ? v[0] : v;
  if (x === "show_all" || x === "hide_learn_more" || x === "hide_tooltips" || x === "hide_all") {
    return x;
  }
  if (x === "show") return "show_all";
  if (x === "hide") return "hide_all";
  return undefined;
}

function parseStored(raw: string | null): HelpTooltipsPreference | undefined {
  if (raw == null) return undefined;
  try {
    return normalizeParsed(JSON.parse(raw) as unknown);
  } catch {
    return undefined;
  }
}

function readInitialStored(): HelpTooltipsPreference | null {
  if (typeof window === "undefined") return null;
  try {
    const v = parseStored(localStorage.getItem(STORAGE_KEY));
    if (v !== undefined) return v;
  } catch {
    /* ignore */
  }
  return null;
}

const [storedHelp, setStoredHelp] = makePersisted(createSignal<HelpTooltipsPreference | null>(readInitialStored()), {
  storage: typeof window !== "undefined" ? localStorage : undefined,
  name: STORAGE_KEY,
});

/**
 * Help UI: {@link StTooltip} popovers vs Learn more / info doc links are controlled separately except in Hide all.
 * When storage is unset (null), defaults: Regular → Show all, Advanced → Hide all.
 *
 * Native HTML `title` tooltips: use `useNativeTooltipTitle()` from `@storytell/ui/tooltip`, or `StButton`’s `title` prop
 * (suppressed with the same flag as `StTooltip` via `StTooltipSuppressionProvider` in the app root).
 */
export const useHelpTooltipsPreference = (): {
  preference: Accessor<HelpTooltipsPreference>;
  setPreference: (v: HelpTooltipsPreference) => void;
  /** `StTooltip` and similar hover popovers (not the Learn more link rows). */
  showStTooltips: () => boolean;
  /** Learn more / info links wrapped with {@link WhenTooltipsAllowed}. */
  showLearnMoreLinks: () => boolean;
} => {
  const { userMode } = useUserMode();

  const preference = createMemo((): HelpTooltipsPreference => {
    const s = storedHelp();
    if (s !== null) return s;
    return userMode() === "advanced" ? "hide_all" : "show_all";
  });

  const setPreference = (v: HelpTooltipsPreference) => {
    setStoredHelp(v);
  };

  const showStTooltips = () => {
    const p = preference();
    return p === "show_all" || p === "hide_learn_more";
  };

  const showLearnMoreLinks = () => {
    const p = preference();
    return p === "show_all" || p === "hide_tooltips";
  };

  return {
    preference,
    setPreference,
    showStTooltips,
    showLearnMoreLinks,
  };
};
